import React, { useState } from 'react';
import Badge from '@amzn/awsui-components-console/badge';
import Box from '@amzn/awsui-components-console/box';
import Container from '@amzn/awsui-components-console/container';
import Link from '@amzn/awsui-components-console/link';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import { TranslationProgress } from '../types';
import styles from '../StringGroupDetail.module.scss';

interface TranslationProgressPanelProps {
  progress: TranslationProgress;
}

/** A locale with its display badge, in table render order. */
type LocaleStatusColor = 'green' | 'blue' | 'grey' | 'red';
interface LocaleStatusRow {
  locale: string;
  label: string;
  color: LocaleStatusColor;
}

/**
 * Flattens the per-status locale lists into a single ordered list for the
 * locales table. Cancelled locales are surfaced as Cancelled
 * per the cancellation UX spec.
 */
function buildLocaleRows(progress: TranslationProgress): LocaleStatusRow[] {
  return [
    ...progress.completedLocales.map(
      (locale): LocaleStatusRow => ({
        locale,
        label: 'Completed',
        color: 'green',
      }),
    ),
    ...progress.inProgressLocales.map(
      (locale): LocaleStatusRow => ({
        locale,
        label: 'In Progress',
        color: 'blue',
      }),
    ),
    ...progress.cancelledLocales.map(
      (locale): LocaleStatusRow => ({
        locale,
        label: 'Cancelled',
        color: 'grey',
      }),
    ),
    ...progress.failedLocales.map(
      (locale): LocaleStatusRow => ({ locale, label: 'Failed', color: 'red' }),
    ),
  ];
}

const TranslationProgressPanel: React.FC<TranslationProgressPanelProps> = ({
  progress,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const totalLocales =
    progress.completedLocales.length +
    progress.inProgressLocales.length +
    progress.failedLocales.length +
    progress.cancelledLocales.length;

  const percentage =
    totalLocales > 0
      ? Math.round((progress.completedLocales.length / totalLocales) * 100)
      : 0;

  const localeRows = buildLocaleRows(progress);

  return (
    <Container
      header={
        <div className={styles.progressHeader}>
          <h3 className={styles.progressHeaderTitle}>Translation Progress</h3>
          <Link href="#" variant="info">
            Need help?
          </Link>
        </div>
      }
    >
      {/* Slim custom progress bar with accessibility */}
      <div
        className={styles.progressBarTrack}
        role="progressbar"
        aria-valuenow={percentage}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Translation progress: ${percentage}% complete`}
      >
        <div
          className={styles.progressBarFill}
          style={{ width: `${percentage}%` }}
        />
      </div>

      {/* Percentage label + show details toggle */}
      <div className={styles.progressLabel}>
        <span>{percentage}%</span>
        <button
          className={styles.showDetailsToggle}
          onClick={() => setShowDetails(!showDetails)}
          type="button"
          aria-label={
            showDetails ? 'Hide locale details' : 'Show locale details'
          }
          aria-expanded={showDetails}
        >
          {showDetails ? '▴ Hide Details' : '▾ Show Details'}
        </button>
      </div>

      {/* Expandable stats grid */}
      {showDetails && (
        <div className={styles.progressStats}>
          <div className={styles.progressStat}>
            <div className={`${styles.statNumber} ${styles.statSuccess}`}>
              {progress.completedLocales.length}
            </div>
            <div className={styles.statLabel}>Locales Completed</div>
          </div>
          <div className={styles.progressStat}>
            <div className={`${styles.statNumber} ${styles.statWarning}`}>
              {progress.inProgressLocales.length}
            </div>
            <div className={styles.statLabel}>In Progress</div>
          </div>
          <div className={styles.progressStat}>
            <div className={styles.statNumber}>
              {progress.cancelledLocales.length}
            </div>
            <div className={styles.statLabel}>Cancelled</div>
          </div>
          <div className={styles.progressStat}>
            <div className={`${styles.statNumber} ${styles.statError}`}>
              {progress.failedLocales.length}
            </div>
            <div className={styles.statLabel}>Failed</div>
          </div>
        </div>
      )}

      {/* Expandable per-locale status table. */}
      {showDetails && localeRows.length > 0 && (
        <Box margin={{ top: 's' }}>
          <SpaceBetween size="xxs">
            {localeRows.map((row) => (
              <div
                key={`${row.locale}-${row.label}`}
                className={styles.localeRow}
              >
                <span>{row.locale}</span>
                <Badge color={row.color}>{row.label}</Badge>
              </div>
            ))}
          </SpaceBetween>
        </Box>
      )}
    </Container>
  );
};

export default TranslationProgressPanel;
